/**
 * Google Drive image operations.
 *
 * Manages the `mini-fridge/gallery` subfolder and provides helpers to:
 *   - find or create the gallery subfolder
 *   - list image files in the gallery
 *   - upload an image Blob to the gallery
 *   - download an image from Drive as a Blob
 *
 * Uses the same `drive.file` scope as the main googleDriveService.
 */

import { getDriveAccountStorageKey } from './googleDriveService';

const DRIVE_API_BASE = 'https://www.googleapis.com/drive/v3';
const DRIVE_UPLOAD_BASE = 'https://www.googleapis.com/upload/drive/v3';

const GALLERY_FOLDER_NAME = 'gallery';
const LS_GALLERY_FOLDER_ID_KEY = 'frostgrave:drive-gallery-folder-id';

export interface DriveImageFile {
  id: string;
  name: string;
  modifiedTime?: string;
  mimeType?: string;
}

// ---------------------------------------------------------------------------
// Internal helper — mirrors the pattern in googleDriveService.ts
// ---------------------------------------------------------------------------

async function driveRequest(
  url: string,
  accessToken: string,
  options?: RequestInit
): Promise<Response> {
  const response = await fetch(url, {
    ...options,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      ...(options?.headers ?? {}),
    },
  });
  if (!response.ok) {
    const errorText = await response.text().catch(() => response.statusText);
    throw new Error(`Drive API ${response.status}: ${errorText}`);
  }
  return response;
}

// ---------------------------------------------------------------------------
// Gallery folder cache
// ---------------------------------------------------------------------------

function getCachedGalleryFolderId(): string | null {
  return localStorage.getItem(getDriveAccountStorageKey(LS_GALLERY_FOLDER_ID_KEY));
}

function setCachedGalleryFolderId(id: string): void {
  localStorage.setItem(getDriveAccountStorageKey(LS_GALLERY_FOLDER_ID_KEY), id);
}

export function clearGalleryFolderCache(): void {
  localStorage.removeItem(getDriveAccountStorageKey(LS_GALLERY_FOLDER_ID_KEY));
}

// ---------------------------------------------------------------------------
// findOrCreateGalleryFolder
// ---------------------------------------------------------------------------

/**
 * Finds or creates the `gallery` subfolder inside the given parent folder
 * (the `mini-fridge` folder from googleDriveService).
 *
 * The folder ID is cached in localStorage to avoid repeated list calls.
 */
export async function findOrCreateGalleryFolder(
  accessToken: string,
  parentFolderId: string
): Promise<string> {
  const cached = getCachedGalleryFolderId();
  if (cached) {
    try {
      await driveRequest(`${DRIVE_API_BASE}/files/${cached}?fields=id`, accessToken);
      return cached;
    } catch {
      // Cached ID no longer valid — fall through to search.
      localStorage.removeItem(getDriveAccountStorageKey(LS_GALLERY_FOLDER_ID_KEY));
    }
  }

  const query = encodeURIComponent(
    `name='${GALLERY_FOLDER_NAME}' and '${parentFolderId}' in parents and mimeType='application/vnd.google-apps.folder' and trashed=false`
  );
  const listRes = await driveRequest(
    `${DRIVE_API_BASE}/files?q=${query}&fields=files(id,name)`,
    accessToken
  );
  const listData = (await listRes.json()) as { files: DriveImageFile[] };

  if (listData.files.length > 0) {
    const folderId = listData.files[0].id;
    setCachedGalleryFolderId(folderId);
    return folderId;
  }

  // Create the gallery subfolder.
  const createRes = await driveRequest(`${DRIVE_API_BASE}/files?fields=id`, accessToken, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name: GALLERY_FOLDER_NAME,
      mimeType: 'application/vnd.google-apps.folder',
      parents: [parentFolderId],
    }),
  });
  const created = (await createRes.json()) as DriveImageFile;
  setCachedGalleryFolderId(created.id);
  return created.id;
}

// ---------------------------------------------------------------------------
// listGalleryImages
// ---------------------------------------------------------------------------

/**
 * Lists all image files in the gallery folder (non-trashed, non-folder items).
 */
export async function listGalleryImages(
  accessToken: string,
  galleryFolderId: string
): Promise<DriveImageFile[]> {
  const query = encodeURIComponent(
    `'${galleryFolderId}' in parents and trashed=false and mimeType != 'application/vnd.google-apps.folder'`
  );
  const res = await driveRequest(
    `${DRIVE_API_BASE}/files?q=${query}&fields=files(id,name,modifiedTime,mimeType)&orderBy=modifiedTime desc`,
    accessToken
  );
  const data = (await res.json()) as { files: DriveImageFile[] };
  return data.files;
}

// ---------------------------------------------------------------------------
// uploadImageToDrive
// ---------------------------------------------------------------------------

/**
 * Uploads an image Blob to the gallery folder using a multipart request so
 * that both the file metadata (name, parent) and the binary content are sent
 * in a single call.
 *
 * Returns the newly created Drive file ID.
 */
export async function uploadImageToDrive(
  accessToken: string,
  galleryFolderId: string,
  fileName: string,
  blob: Blob
): Promise<string> {
  const mimeType = blob.type || 'image/png';
  const boundary = 'fg_image_boundary';

  const metadata = JSON.stringify({ name: fileName, parents: [galleryFolderId] });

  // Build multipart body: metadata part + binary part.
  const metaPart = `--${boundary}\r\nContent-Type: application/json; charset=UTF-8\r\n\r\n${metadata}\r\n`;
  const closingBoundary = `\r\n--${boundary}--`;
  const binaryPart = `--${boundary}\r\nContent-Type: ${mimeType}\r\n\r\n`;

  // Concatenate as Uint8Arrays so the binary content is not corrupted.
  const encoder = new TextEncoder();
  const metaBytes = encoder.encode(metaPart);
  const binaryHeaderBytes = encoder.encode(binaryPart);
  const blobBytes = new Uint8Array(await blob.arrayBuffer());
  const closingBytes = encoder.encode(closingBoundary);

  const body = new Uint8Array(
    metaBytes.length + binaryHeaderBytes.length + blobBytes.length + closingBytes.length
  );
  let offset = 0;
  body.set(metaBytes, offset);        offset += metaBytes.length;
  body.set(binaryHeaderBytes, offset); offset += binaryHeaderBytes.length;
  body.set(blobBytes, offset);         offset += blobBytes.length;
  body.set(closingBytes, offset);

  const res = await driveRequest(
    `${DRIVE_UPLOAD_BASE}/files?uploadType=multipart&fields=id`,
    accessToken,
    {
      method: 'POST',
      headers: { 'Content-Type': `multipart/related; boundary=${boundary}` },
      body,
    }
  );
  const data = (await res.json()) as DriveImageFile;
  return data.id;
}

// ---------------------------------------------------------------------------
// downloadImageFromDrive
// ---------------------------------------------------------------------------

/**
 * Downloads a Drive file as a Blob (using `alt=media`).
 */
export async function downloadImageFromDrive(
  accessToken: string,
  fileId: string
): Promise<Blob> {
  const res = await driveRequest(
    `${DRIVE_API_BASE}/files/${fileId}?alt=media`,
    accessToken
  );
  return res.blob();
}
